import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { GraduationCap, Calendar, IdCard, Loader2, ArrowLeft, Users, School } from 'lucide-react';
import { toast } from "sonner";

const API_URL = '';

// Resilient fetch with retry for Neon DB cold starts
async function resilientFetch(url, options = {}, maxRetries = 3) {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 25000);
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      if (response.status >= 500 && attempt < maxRetries) {
        await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
        continue;
      }
      return response;
    } catch (error) {
      if (attempt < maxRetries && (error.name === 'AbortError' || error.message === 'Failed to fetch')) {
        await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
        continue;
      }
      throw error;
    }
  }
}

export default function StudentLoginPage() {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [formData, setFormData] = useState({
    schoolId: '',
    birthRegistration: '',
    dateOfBirth: ''
  });
  const [loading, setLoading] = useState(false);
  const [schoolsLoading, setSchoolsLoading] = useState(true);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('expired')) toast.error('Your session has expired. Please sign in again.');
  }, []);

  useEffect(() => {
    // Warm up DB connection
    fetch(`${API_URL}/api/warmup`).catch(() => {});
    // Show cached schools instantly while fetching fresh data
    const cached = localStorage.getItem('cachedStudentSchools');
    if (cached) {
      try {
        const cachedSchools = JSON.parse(cached);
        if (cachedSchools.length > 0) {
          setSchools(cachedSchools);
          setSchoolsLoading(false);
        }
      } catch(e) {}
    }
    loadSchools();
  }, []);

  const loadSchools = async (retryCount = 0) => {
    try {
      const response = await resilientFetch(`${API_URL}/api/student-portal/schools`);
      if (response.ok) {
        const data = await response.json();
        const schoolList = data.schools || [];
        setSchools(schoolList);
        setSchoolsLoading(false);
        localStorage.setItem('cachedStudentSchools', JSON.stringify(schoolList));
      } else if (retryCount < 5) {
        setTimeout(() => loadSchools(retryCount + 1), 2000);
      } else {
        // Fall back to cached data
        const cached = localStorage.getItem('cachedStudentSchools');
        if (cached) {
          try { setSchools(JSON.parse(cached)); } catch(e) {}
        }
        setSchoolsLoading(false);
      }
    } catch (error) {
      console.error('Failed to load schools:', error);
      if (retryCount < 5) {
        setTimeout(() => loadSchools(retryCount + 1), 2000);
      } else {
        const cached = localStorage.getItem('cachedStudentSchools');
        if (cached) {
          try { setSchools(JSON.parse(cached)); } catch(e) {}
        }
        setSchoolsLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.schoolId) {
      toast.error('Please select your school');
      return;
    }
    if (!formData.birthRegistration || !formData.dateOfBirth) {
      toast.error('Please enter both Birth Registration Number and Date of Birth');
      return;
    }

    setLoading(true);

    try {
      let response;
      for (let attempt = 0; attempt < 3; attempt++) {
        response = await resilientFetch(`${API_URL}/api/student-portal/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            school_id: formData.schoolId,
            birth_registration: formData.birthRegistration,
            date_of_birth: formData.dateOfBirth
          }),
        });
        if (response.ok || (response.status >= 400 && response.status < 500)) break;
        await new Promise(r => setTimeout(r, 2000));
      }

      if (!response.ok) {
        let errorMsg = 'Login failed';
        const ct = response.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          try { const err = await response.json(); errorMsg = err.detail || errorMsg; } catch(e) {}
        }
        throw new Error(errorMsg);
      }

      const data = await response.json();
      
      localStorage.setItem('student_token', data.access_token);
      localStorage.setItem('student_user', JSON.stringify(data.user));
      
      toast.success(`Welcome, ${data.user.name}!`);
      navigate('/student/dashboard');
    } catch (error) {
      console.error('Student login error:', error);
      toast.error(error.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-amber-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-gradient-to-br from-amber-400 to-amber-600 p-4 rounded-2xl shadow-xl shadow-amber-500/30 transform hover:scale-105 transition-transform">
              <GraduationCap className="h-12 w-12 text-emerald-900" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Student Portal</h1>
          <p className="text-amber-200">Access your academic information</p>
        </div>

        <Card className="shadow-2xl bg-white/95 backdrop-blur-sm border-0 overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-emerald-500 via-amber-400 to-emerald-500" />
          <CardHeader className="space-y-1 pt-6">
            <CardTitle className="text-2xl text-center text-emerald-900" data-testid="student-login-title">
              Student Login
            </CardTitle>
            <CardDescription className="text-center text-gray-600">
              Select your school and enter your credentials
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="student-login-form">
              {!schoolsLoading && schools.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6 space-y-3">
                  <p className="text-sm text-gray-600 text-center">Could not load schools. The server may still be starting up.</p>
                  <Button type="button" variant="outline" onClick={() => { setSchoolsLoading(true); loadSchools(); }}>
                    Try Again
                  </Button>
                </div>
              ) : (
              <>
              <div className="space-y-2">
                <Label htmlFor="school" className="text-emerald-800 font-medium flex items-center gap-2">
                  <School className="h-4 w-4" />
                  School
                </Label>
                <select
                  id="school"
                  value={formData.schoolId}
                  onChange={(e) => setFormData({ ...formData, schoolId: e.target.value })}
                  disabled={loading || schoolsLoading}
                  className="flex h-12 w-full rounded-md border border-emerald-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="school-select"
                >
                  <option value="">
                    {schoolsLoading ? 'Loading schools...' : '-- Select your school --'}
                  </option>
                  {schools.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="birthRegistration" className="text-emerald-800 font-medium flex items-center gap-2">
                  <IdCard className="h-4 w-4" />
                  Birth Registration Number
                </Label>
                <Input
                  id="birthRegistration"
                  type="text"
                  placeholder="e.g., 123456"
                  value={formData.birthRegistration}
                  onChange={(e) => setFormData({ ...formData, birthRegistration: e.target.value })}
                  disabled={loading}
                  className="h-12 border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500"
                  data-testid="birth-registration-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dateOfBirth" className="text-emerald-800 font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Date of Birth
                </Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                  disabled={loading}
                  min="1990-01-01"
                  max="2022-12-31"
                  className="h-12 border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500"
                  data-testid="dob-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-semibold shadow-lg shadow-emerald-500/30 transition-all"
                disabled={loading}
                data-testid="student-login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  'Access Portal'
                )}
              </Button>
              </>
              )}
            </form>

            <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800 text-center">
                Contact your school office if you don't know your Birth Registration Number.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 flex flex-col items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate('/parent/login')}
            className="text-amber-200 hover:text-white hover:bg-white/10"
            data-testid="parent-portal-link"
          >
            <Users className="h-4 w-4 mr-2" />
            Parent Portal
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-emerald-200 hover:text-white hover:bg-white/10"
            data-testid="back-home-link"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
